import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { Box, Chip, Typography } from '@mui/material';
import React from 'react';

import { getProduct } from '../subscriptions/catalog';
import { getProductIcon } from '../subscriptions/entitlementIcons';
import type { MembershipState } from '../subscriptions/types';

export interface MembershipStatusCardProps {
  membership?: MembershipState | null;
}

function formatDate(iso: string | null): string | null {
  if (!iso) {
    return null;
  }
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  return parsed.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Current membership for one assignment. Renders nothing when there has never
 * been a membership -- the purchase page handles that case.
 */
const MembershipStatusCard: React.FC<MembershipStatusCardProps> = ({ membership }) => {
  if (!membership || membership.status === 'none') {
    return null;
  }

  const product = membership.product_id ? getProduct(membership.product_id) : undefined;
  const Icon = membership.product_id ? getProductIcon(membership.product_id) : CheckCircleIcon;
  const periodEnd = formatDate(membership.current_period_end);

  const { label, color, chipIcon } = statusChip(membership);
  const title = membership.org_provided
    ? 'Provided by your organization'
    : product?.name ?? 'Membership';

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        p: 2,
        mb: 2,
        borderRadius: 2,
        border: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
      data-testid="membership-status-card"
      data-attr={`membership-status--${membership.status}`}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 40,
            height: 40,
            borderRadius: '50%',
            bgcolor: 'secondary.main',
            color: 'secondary.contrastText',
            flexShrink: 0,
          }}
        >
          <Icon fontSize="small" />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontWeight: 600, letterSpacing: 0.6, textTransform: 'uppercase' }}
          >
            Current membership
          </Typography>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
          {periodEnd && (
            <Typography variant="body2" color="text.secondary">
              {membershipPeriodLabel(membership, periodEnd)}
            </Typography>
          )}
        </Box>
      </Box>
      <Chip
        icon={chipIcon}
        label={label}
        size="small"
        color={color}
        variant="outlined"
        sx={{ flexShrink: 0 }}
      />
    </Box>
  );
};

function statusChip(membership: MembershipState): {
  label: string;
  color: 'success' | 'warning' | 'default';
  chipIcon: React.ReactElement;
} {
  if (membership.status === 'trialing') {
    return { label: 'Free trial', color: 'warning', chipIcon: <ScheduleIcon /> };
  }
  if (membership.status === 'expired') {
    return { label: 'Expired', color: 'default', chipIcon: <ErrorOutlineIcon /> };
  }
  return { label: 'Active', color: 'success', chipIcon: <CheckCircleIcon /> };
}

function membershipPeriodLabel(membership: MembershipState, periodEnd: string): string {
  if (membership.status === 'expired') {
    return `Ended ${periodEnd}`;
  }
  if (membership.status === 'trialing') {
    return `Free trial ends ${periodEnd}`;
  }
  if (membership.cancel_at_period_end) {
    return `Access ends ${periodEnd}`;
  }
  return `Renews ${periodEnd}`;
}

export default MembershipStatusCard;
